import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Ticket, TicketUpdate } from "@models/Ticket";
import { ModelBase, User } from "@models";

import API from "@dndUtilities/system_api";

import LoadingBox from "@components/input/LoadingBox";
import StringBox from "@components/input/strings/StringBox";
import DescriptionBox from "@dndComponents/DescriptionBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ArrowBackButton from "@components/input/buttons/icon_buttons/ArrowBackButton";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import ButtonBox from "@components/input/buttons/ButtonBox";

const api = API.getInstance();
// const common_util = DataUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function TicketEdit({ obj: param_obj }: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Ticket>(param_obj instanceof Ticket ? param_obj : api.applyWorkingOn(new Ticket()));
  const [users, setUsers] = useState<User[]>([]);
  const [expanded, setExpanded] = useState("");
  const [ping, setPing] = useState(false);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  const load = useCallback( async () => {
    const user_ids = obj.user_tickets.map(o => o.owner_id);
    const res3 = await api.getObjects({ data_type: "user", filter: { "_id*ObjectIDs": user_ids } });
    setUsers(res3 as User[]);
    setLoading(false);
  }, [obj]);

  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [load, fetched]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const submit = () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (!api.user.admin || obj.source_books_old.filter(o => o.id === "Personal").length > 0) {
        obj.owner_id = api.user._id;
      } else if (api.user.admin && obj.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
        obj.owner_id = "";
      }
      api.upsertObject("ticket", obj).then(res => {
        console.log(res);
        if (res && res.body.id) {
          obj._id = res.body.id;
        } 
        setProcessing(false);
        setNavigateTo("/ticket");
      });
    }
  }

  const renderUpdates = () => {
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          Updates&nbsp;
          <AddButton 
            onClick={() => {
              const new_obj = obj;
              new_obj.updates.push(new TicketUpdate());
              setObj(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row" className="grid-striped">
            { obj.updates.map(u => {
              return (
                <Grid
                  key={u.id}
                  size={{
                    xs: 12
                  }}>
                  <Grid container spacing={0} direction="row">
                    <Grid
                      size={{
                        xs: 9
                      }}>
                      <StringBox
                        name="Update"
                        value={u.text}
                        disabled={disabled}
                        onBlur={(changed: string) => {
                          u.text = changed;
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <DeleteButton
                        onClick={() => {
                          const new_obj = obj;
                          new_obj.updates = new_obj.updates.filter(o => o.id !== u.id);
                          setObj(new_obj);
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </>
    );
  }

  const renderUserTickets = () => {
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          Associated User Tickets
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row" className="grid-striped">
            { obj.user_tickets.map(ut => {
              const user = users.find(o => o._id === ut.owner_id);
              return (
                <Grid
                  key={ut._id}
                  size={{
                    xs: 12
                  }}>
                  <Grid container spacing={0} direction="row">
                    <Grid
                      className="clickable"
                      onClick={() => { setExpanded(expanded === ut._id ? "" : ut._id); }}
                      size={{
                        xs: 9
                      }}>
                      <span className="header">{ ut.ticket_type }</span>: { ut.subject } { user ? `(${user.profile_name})` : "" }
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <DeleteButton
                        label="Remove Association"
                        onClick={() => {
                          const new_obj = obj;
                          new_obj.user_ticket_ids = new_obj.user_ticket_ids.filter(o => o !== ut._id);
                          new_obj.user_tickets = new_obj.user_tickets.filter(o => o._id !== ut._id);
                          setObj(new_obj);
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                    { expanded === ut._id &&
                      <Grid
                        style={{ background: "black", border: "1px solid" }}
                        size={{
                          xs: 12
                        }}>
                        <Grid container spacing={0} direction="row">
                          <Grid
                            size={{
                              xs: 12
                            }}>
                            { ut.text }
                          </Grid>
                          <Grid
                            size={{
                              xs: 12
                            }}>
                            <ButtonBox 
                              name="Go To User Ticket"
                              onClick={() => {
                                setNavigateTo(`/user_ticket/${ut._id}`);
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    }
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </>
    );
  }
  
  const height = window.innerHeight - 64;
  if (!fetched || loading) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          <ArrowBackButton 
            fab
            onClick={() => { setNavigateTo("/ticket"); }}
          />
          &nbsp;
          Ticket
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Subject"
            value={obj.name} 
            message={obj.name.length > 0 ? "" : "Subject Invalid"}
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.name = value;
              setObj(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          style={{ height: "calc(100vh - 205px)", overflowY: "auto" }}
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DescriptionBox
                name="Text"
                value={obj.description}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.description = changed;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <SelectStringBox
                name="Status"
                value={obj.status}
                options={["New","Assigned","In Progress","Awaiting Deploy","Complete"]}
                disabled={disabled}
                onChange={(changed: "New" | "Assigned" | "In Progress" | "Awaiting Deploy" | "Complete") => {
                  const new_obj = obj;
                  new_obj.status = changed;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            { renderUpdates() }
            { renderUserTickets() }
          </Grid>
        </Grid>
        { !disabled &&
          <Grid
            size={{
              xs: 12
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={processing}
              onClick={submit}>
              Submit
            </Button>
            <Button
              variant="contained"
              disabled={processing}
              style={{ marginLeft: "4px" }}
              onClick={ () => { 
                setNavigateTo(`/ticket`);
              }}>
              Cancel
            </Button>
          </Grid>
        }
      </Grid>
    );
  }
}